import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import TimerCard, { TimerMode } from '../molecules/TimerCard';
import TimerForm from '../molecules/TimerForm';
import Button from '../atoms/Button';
import { FaPlus } from 'react-icons/fa';
import { EffectType } from '../atoms/TimerEffect';

// 更新计时器数据接口，添加特效相关字段
export interface TimerData {
  id: string;
  name: string;
  hours: number;
  minutes: number;
  seconds: number;
  mode: TimerMode;
  showMilliseconds: boolean;
  effectType?: EffectType;
  effectOpacity?: number;
  effectColor?: string;
  effectSecondaryColor?: string;
  effectSpeed?: number;
  createdAt: number;
}

interface TimersContainerProps {
  timers: TimerData[];
  onAddTimer: (timer: Omit<TimerData, 'id' | 'createdAt'>) => void;
  onDeleteTimer: (id: string) => void;
  onUpdateTimer?: (id: string, timer: Partial<Omit<TimerData, 'id' | 'createdAt'>>) => void;
  className?: string;
}

const TimersContainer: React.FC<TimersContainerProps> = ({
  timers,
  onAddTimer,
  onDeleteTimer,
  onUpdateTimer,
  className = '',
}) => {
  // 状态
  const [showForm, setShowForm] = useState(false);
  const { t } = useTranslation();
  
  // 处理添加计时器 - 更新参数类型，包含特效相关字段
  const handleAddTimer = (timerData: {
    name: string;
    hours: number;
    minutes: number;
    seconds: number;
    mode: TimerMode;
    showMilliseconds: boolean;
    effectType: EffectType;
    effectOpacity: number;
    effectColor?: string;
    effectSecondaryColor?: string;
    effectSpeed: number;
  }) => {
    onAddTimer(timerData);
    setShowForm(false);
  };
  
  // 处理删除计时器
  const handleDeleteTimer = (id: string, confirmed?: boolean) => {
    // 如果已经确认过，则跳过确认对话框
    if (confirmed || window.confirm(t('confirm') + ' ' + t('delete') + '?')) {
      onDeleteTimer(id);
    }
  };
  
  // 处理编辑计时器 - 更新参数类型，包含特效相关字段
  const handleEditTimer = (id: string, data: {
    name: string;
    hours: number;
    minutes: number;
    seconds: number;
    mode: TimerMode;
    showMilliseconds: boolean;
    effectType: EffectType;
    effectOpacity: number;
    effectColor?: string;
    effectSecondaryColor?: string;
    effectSpeed: number;
  }) => {
    if (onUpdateTimer) {
      onUpdateTimer(id, data);
    }
  };
  
  // 处理计时器完成
  const handleTimerComplete = (id: string) => {
    console.log(`${t('timerComplete')} ${id}`);
    // 这里可以添加通知或其他完成后的逻辑
  };
  
  return (
    <div className={`${className}`}>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('myTimers')}</h2>
        <Button
          variant="accent"
          icon={<FaPlus />}
          onClick={() => setShowForm(true)}
          disabled={showForm}
        >
          {t('addTimer')}
        </Button>
      </div>
      
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 overflow-hidden"
          >
            <TimerForm
              onSubmit={handleAddTimer}
              onCancel={() => setShowForm(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>
      
      {timers.length === 0 && !showForm ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-8 text-center"
        >
          <p className="text-gray-500 dark:text-gray-400 mb-4">{t('noTimers')}</p>
          <Button
            variant="primary"
            icon={<FaPlus />}
            onClick={() => setShowForm(true)}
          >
            {t('createFirstTimer')}
          </Button>
        </motion.div>
      ) : (
        <div className="timers-grid">
          {timers.map((timer) => (
            <TimerCard
              key={timer.id}
              id={timer.id}
              name={timer.name}
              initialHours={timer.hours}
              initialMinutes={timer.minutes}
              initialSeconds={timer.seconds}
              mode={timer.mode}
              showMilliseconds={timer.showMilliseconds}
              effectType={timer.effectType}
              effectOpacity={timer.effectOpacity}
              effectColor={timer.effectColor}
              effectSecondaryColor={timer.effectSecondaryColor}
              effectSpeed={timer.effectSpeed}
              onComplete={() => handleTimerComplete(timer.id)}
              onDelete={handleDeleteTimer}
              onEdit={handleEditTimer}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TimersContainer;